import React from 'react';

import { useChartTheme } from '@/shared/lib/chart-theme';
import { roundToNext100, roundToPrev100 } from '@/shared/lib/number';
import {
  BarElement,
  CategoryScale,
  Chart,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  type ChartOptions,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

interface MonthlyChangeData {
  month: string;
  changePct: number;
}

interface DivergingBarChartProps {
  data: MonthlyChangeData[];
  chartLabelText?: string;
}

/**
 * A factory rather than a constant because the track color is theme-dependent
 * and a plugin gets no access to React state.
 */
export const createDivergingBarBackgroundPlugin = (trackColor: string) => ({
  id: 'barBackground',
  beforeDatasetsDraw: (chart: Chart<'bar'>) => {
    const {
      ctx,
      scales: { y },
    } = chart;

    chart.data.datasets.forEach((_, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.type !== 'bar') return;

      meta.data.forEach((bar) => {
        const barElement = bar as BarElement;
        const { x: barElementX, width: barElementWidth } = barElement.getProps(
          ['x', 'width'],
          true
        );

        if (!barElementX || !barElementWidth) return;

        const xPos = barElementX - barElementWidth / 2;
        const width = barElementWidth;

        const yTop = y.getPixelForValue(y.max);
        const yBottom = y.getPixelForValue(y.min);

        ctx.save();
        ctx.fillStyle = trackColor;
        ctx.fillRect(xPos, yTop, width, yBottom - yTop);
        ctx.restore();
      });
    });
  },
});

export const DivergingBarChart: React.FC<DivergingBarChartProps> = ({
  data,
  chartLabelText = '',
}) => {
  const chartTheme = useChartTheme();

  const labels = data.map((d) => d.month);
  const values = data.map((d) => d.changePct);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);

  // Add extra ±100 padding to Y-axis only if values cross 0
  // - Prevents clipping when bars are near min/max
  // - Skips padding when all values are strictly positive or strictly negative
  //   (avoids unnecessary empty space on the chart)
  const yMax = roundToNext100(maxValue) + (maxValue > 0 ? 100 : 0);
  const yMin = roundToPrev100(minValue) - (minValue < 0 ? 100 : 0);

  const chartData = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: data.map((d) =>
          d.changePct >= 0 ? 'rgba(255, 77, 79, 1)' : 'rgba(115, 209, 61, 1)'
        ),
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
      datalabels: {
        anchor: 'end',
        align: 'end',
        formatter: (value: number) => `${value}%`,
        // These land on the bar track, not on the red/green bar, so they follow
        // the track's theme rather than the bar color.
        color: chartTheme.dataLabel,
      },
    },
    scales: {
      x: {
        grid: {
          drawTicks: false,
        },
        border: {
          dash: [2, 3],
        },
        ticks: {
          color: chartTheme.axisText,
        },
      },
      y: {
        title: {
          display: true,
          text: chartLabelText,
          color: chartTheme.axisText,
        },
        min: yMin,
        max: yMax,
        ticks: {
          color: chartTheme.axisText,
          callback: (value: number | string) => {
            if (value === yMin || value === yMax || value === 0) {
              return `${value}`;
            }
            return '';
          },
        },
        grid: {
          drawTicks: false,
          color: (ctx) => {
            if ([yMin, 0, yMax].includes(ctx.tick.value)) {
              return chartTheme.gridLine;
            }
            return 'transparent';
          },
        },
      },
    },
  };

  return (
    <Bar
      data={chartData}
      options={options}
      plugins={[createDivergingBarBackgroundPlugin(chartTheme.barTrack)]}
    />
  );
};
